import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Select,
  Space,
  message,
  Modal,
  Skeleton,
  Divider,
  Table,
  Popconfirm,
} from "antd";
import { useHistory, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

import axiosInstance from "../../../configs";
import InvoiceHeader from "./Shared/InvoiceHeader";
import CustomerSection from "./Shared/CustomerSection";
import ServiceTable from "./Shared/ServiceTable";
import TotalSummary from "./Shared/TotalSummary";

const { Title, Text } = Typography;

export default function CukaiEdit({ invoiceData }) {
  const history = useHistory();
  const { id } = useParams();

  const [, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  const [contacts, setContacts] = useState([]);

  const [selectedContact, setSelectedContact] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [statusGrup, setStatusGrup] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);

  // DETIL ITEM state
  const [nopenItems, setNopenItems] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [selectedItemCode, setSelectedItemCode] = useState("");

  useEffect(() => {
    fetchReferences({ invoice: invoiceData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData]);

  async function fetchReferences({ invoice }) {
    setInitLoading(true);
    try {
      // Robust fetch: handle partial failures
      let contactsData = [];

      try {
        const resContacts = await axiosInstance.get("/finance/contacts");
        contactsData = resContacts.data.data || [];
      } catch (e) {}

      setContacts(contactsData);

      if (invoice) {
        if (
          invoice.items &&
          invoice.items.length > 0 &&
          lineItems.length === 0
        ) {
          const savedItems = invoice.items.map((d, idx) => ({
            id: d.id_detail || `saved-${idx}`,
            name: d.item_name || "",
            description: d.document_number || "",
            documentNumber: d.document_number || null,
            documentDate: d.document_date ? dayjs(d.document_date) : null,
            qty: Number(d.qty || 0),
            price: Number(d.unit_price || 0),
            taxRate: Number(d.tax_rate || 0),
            taxAmount: Number(d.tax_amount || 0),
            amountBeforeTax: Number(d.sub_amount || 0),
            total: Number(d.total_amount || 0),
          }));
          setLineItems(savedItems);
        }

        if (invoice.invoice_items) setInvoiceItems(invoice.invoice_items);

        if (invoice.nopen) {
          try {
            const resItems = await axiosInstance.get(
              `/finance/nopen-items/${invoice.nopen}`,
            );
            setNopenItems(resItems.data.data || []);
          } catch (e) {
            /* empty */
          }
        }
      }
    } catch (err) {
    } finally {
      setInitLoading(false);
    }
  }

  const initialValues = useMemo(() => {
    if (!invoiceData)
      return {
        invoiceNumber: "",
        invoiceDate: dayjs(),
        dueDate: dayjs(),
        periodStart: dayjs(),
        periodEnd: dayjs(),
        documentNumber: "",
        documentDate: dayjs(),
      };
    return {
      invoiceNumber: invoiceData.invoice_number,
      invoiceDate: invoiceData.invoice_date
        ? dayjs(invoiceData.invoice_date)
        : dayjs(),
      dueDate: invoiceData.due_date ? dayjs(invoiceData.due_date) : dayjs(),
      periodStart: invoiceData.period_start
        ? dayjs(invoiceData.period_start)
        : null,
      periodEnd: invoiceData.period_end ? dayjs(invoiceData.period_end) : null,
      documentNumber: invoiceData.nopen || "",
      documentDate: invoiceData.document_date
        ? dayjs(invoiceData.document_date)
        : null,
    };
  }, [invoiceData]);

  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  function setFieldValue(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  useEffect(() => {
    if (invoiceData && contacts.length > 0) {
      const matchedContact = contacts.find(
        (c) => String(c.idContact) === String(invoiceData.id_customer),
      );

      if (matchedContact) {
        setSelectedContact(matchedContact);
        setStatusGrup(matchedContact.contactType === 0 ? "Grup" : "Non Grup"); // 0 is Grup
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData, contacts]);

  function handleRemoveItem(idx) {
    const ni = [...lineItems];
    ni.splice(idx, 1);
    setLineItems(ni);
  }

  function handleQtyChange(idx, val) {
    const ni = [...lineItems];
    ni[idx].qty = val;
    ni[idx].amountBeforeTax = val * ni[idx].price;
    ni[idx].taxAmount = ni[idx].amountBeforeTax * (ni[idx].taxRate / 100);
    ni[idx].total = ni[idx].amountBeforeTax + ni[idx].taxAmount;
    setLineItems(ni);
  }

  const totalAll = lineItems.reduce(
    (acc, curr) => acc + curr.amountBeforeTax,
    0,
  );
  const totalPajak = lineItems.reduce((acc, curr) => acc + curr.taxAmount, 0);

  async function handleSubmit() {
    setLoading(true);
    try {
      const payload = {
        ...values,
        items: lineItems.map((p) => ({
          document_number: p.documentNumber,
          document_date: p.documentDate?.format("YYYY-MM-DD") || null,
          item_name: p.name,
          qty: p.qty,
          unit_price: p.price,
          sub_amount: p.amountBeforeTax || 0,
          tax_rate: p.taxRate || 0,
          tax_amount: p.taxAmount || 0,
          total_amount: p.total,
        })),
        totalAmount: totalAll,
        invoiceDate: values.invoiceDate.format("YYYY-MM-DD"),
        dueDate: values.dueDate.format("YYYY-MM-DD"),
        periodStart: null,
        periodEnd: null,
      };

      if (id) {
        await axiosInstance.put(`/finance/sales-invoices/${id}`, payload);
        message.success("Invoice berhasil disimpan!");
        setTimeout(() => history.push("/finance"), 1500);
      }
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan invoice");
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  }

  function getInvoiceTypeLabel() {
    return "Cukai";
  }

  // DETIL ITEM handlers
  const availableItems = nopenItems.filter(
    (ni) =>
      !invoiceItems.some(
        (ii) =>
          ii.item_code === ni.item_code && ii.lokasi_lot === ni.lokasi_lot,
      ),
  );

  async function handleAddInvoiceItem() {
    if (!selectedItemCode || !id) return;
    const item = nopenItems.find(
      (i) => `${i.item_code}|${i.lokasi_lot}` === selectedItemCode,
    );
    if (!item) return;
    try {
      const res = await axiosInstance.post("/finance/invoice-items", {
        id_invoice: Number(id),
        item_code: item.item_code,
        item_name: item.item_name,
        jumlah_kemasan: item.jumlah_kemasan,
        lokasi_lot: item.lokasi_lot,
      });
      setInvoiceItems((prev) => [
        ...prev,
        { id_invoice_item: res.data.data.id_invoice_item, ...item },
      ]);
      setSelectedItemCode("");
      message.success("Item ditambahkan");
    } catch (err) {
      message.error("Gagal menambahkan item");
    }
  }

  async function handleDeleteInvoiceItem(itemId) {
    try {
      await axiosInstance.delete(`/finance/invoice-items/${itemId}`);
      setInvoiceItems((prev) =>
        prev.filter((i) => i.id_invoice_item !== itemId),
      );
      message.success("Item dihapus");
    } catch (err) {
      message.error("Gagal menghapus item");
    }
  }

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Title level={3}>Edit Invoice Cukai</Title>
        <Text type="secondary">Finance / Edit / Cukai</Text>
      </div>

      <Card>
        {initLoading ? (
          <Skeleton active />
        ) : (
          <div>
            <Row gutter={[16, 24]}>
              <Col span={24}>
                <InvoiceHeader
                  invoiceData={invoiceData}
                  values={values}
                  setFieldValue={setFieldValue}
                  getInvoiceTypeLabel={getInvoiceTypeLabel}
                />
              </Col>

              <Col span={24}>
                <CustomerSection
                  selectedContact={selectedContact}
                  statusGrup={statusGrup}
                />
              </Col>

              <Col span={24}>
                <Divider />
              </Col>

              {/* Service Selection - CUKAI ONLY */}
              <Col span={24}>
                <Title level={5} style={{ marginBottom: 16 }}>
                  LAYANAN CUKAI
                </Title>
              </Col>

              <Col span={24}>
                <ServiceTable
                  items={lineItems}
                  handleQtyChange={handleQtyChange}
                  handleRemoveItem={handleRemoveItem}
                />
              </Col>

              <Col span={24}>
                <Divider />
              </Col>

              {/* DETIL ITEM */}
              <Col span={24}>
                <Title level={5} style={{ marginBottom: 16 }}>
                  DETIL ITEM
                </Title>
                <Space
                  style={{ width: "100%", marginBottom: 12 }}
                  size="middle"
                >
                  <Select
                    style={{ width: 500 }}
                    value={selectedItemCode}
                    onChange={(val) => setSelectedItemCode(val)}
                    placeholder="Pilih item dari nopen..."
                    showSearch
                    optionFilterProp="children"
                  >
                    {availableItems.map((item, idx) => (
                      <Select.Option
                        key={`${item.item_code}|${item.lokasi_lot}|${idx}`}
                        value={`${item.item_code}|${item.lokasi_lot}`}
                      >
                        {item.item_code} — {item.item_name} (Lot:{" "}
                        {item.lokasi_lot || "-"}, Qty: {item.jumlah_kemasan})
                      </Select.Option>
                    ))}
                  </Select>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={handleAddInvoiceItem}
                    disabled={!selectedItemCode}
                  >
                    Tambah Item
                  </Button>
                </Space>
                <Table
                  dataSource={invoiceItems}
                  rowKey={(r) =>
                    r.id_invoice_item || `${r.item_code}-${r.lokasi_lot}`
                  }
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: "Kode Barang",
                      dataIndex: "item_code",
                      width: 150,
                    },
                    { title: "Nama Barang", dataIndex: "item_name" },
                    {
                      title: "Jumlah Kemasan",
                      dataIndex: "jumlah_kemasan",
                      width: 140,
                      align: "center",
                    },
                    {
                      title: "Lokasi Lot",
                      dataIndex: "lokasi_lot",
                      width: 120,
                    },
                    {
                      title: "#",
                      key: "action",
                      width: 60,
                      align: "center",
                      render: (_, record) => (
                        <Popconfirm
                          title="Hapus item ini?"
                          onConfirm={() =>
                            handleDeleteInvoiceItem(record.id_invoice_item)
                          }
                        >
                          <Button
                            size="small"
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      ),
                    },
                  ]}
                  locale={{
                    emptyText: "Belum ada item. Pilih dari dropdown di atas.",
                  }}
                />
              </Col>

              <Col span={24}>
                <Divider />
              </Col>

              <Col span={24}>
                <TotalSummary
                  totalAll={totalAll}
                  totalPajak={totalPajak}
                  otherAmount={invoiceData?.other_amount}
                />
              </Col>

              <Col span={24} style={{ textAlign: "right" }}>
                <Space>
                  <Button size="large" onClick={() => history.push("/finance")}>
                    Batal
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setConfirmOpen(true)}
                  >
                    Simpan
                  </Button>
                </Space>
              </Col>
            </Row>

            <Modal
              title="Konfirmasi Simpan"
              open={confirmOpen}
              onOk={handleSubmit}
              onCancel={() => setConfirmOpen(false)}
              okText="Ya, Simpan"
              cancelText="Batal"
            >
              <p>Apakah anda yakin ingin menyimpan perubahan invoice ini?</p>
            </Modal>
          </div>
        )}
      </Card>
    </>
  );
}
